import { Link } from 'react-router-dom';
import { LegalPageLayout, LegalSection } from '@/modules/marketing/components/legal-page-layout';
import { usePageSeo } from '@/modules/marketing/hooks/use-page-seo';

const LAST_UPDATED = 'September 7, 2026';

export function TermsPage() {
    usePageSeo({
        title: 'Terms & Conditions',
        description:
            "Read AIPA's Terms & Conditions governing use of our AI Assistant, Pricing Intelligence, Sentiment Analysis, and CRM platform.",
        keywords: 'AIPA terms and conditions, AIPA terms of service, AIPA legal, SaaS terms of use',
    });

    return (
        <LegalPageLayout title="Terms &amp; Conditions" lastUpdated={LAST_UPDATED}>
            <LegalSection title="1. Agreement to Terms">
                <p>
                    These Terms &amp; Conditions ("Terms") govern access to and use of AIPA's website, AI Assistant chat widget,
                    Pricing Intelligence tools, Sentiment Analysis tools, CRM, and related services (together, the "Service"),
                    provided by AIPA ("AIPA," "we," "us," or "our"). By creating an account, embedding our AI Assistant widget, or
                    otherwise using the Service, you agree to be bound by these Terms. If you are using the Service on behalf of a
                    company or other legal entity, you represent that you have the authority to bind that entity, in which case
                    "you" refers to that entity.
                </p>
            </LegalSection>

            <LegalSection title="2. The Service">
                <p>
                    AIPA is a multi-tenant SaaS platform that provides an embeddable AI-powered chat assistant for lead capture,
                    pricing intelligence and competitor rate tracking, review and sentiment monitoring, and a built-in CRM for
                    managing the leads and conversations the Service generates. Features available to your account depend on your
                    subscription plan and the modules enabled for your workspace.
                </p>
            </LegalSection>

            <LegalSection title="3. Accounts &amp; Workspaces">
                <ul className="space-y-2">
                    <li>You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account.</li>
                    <li>You must provide accurate information when creating an account or workspace, and keep it up to date.</li>
                    <li>A workspace administrator may invite, manage, and remove other users within their own workspace.</li>
                    <li>You must notify us promptly of any unauthorized use of your account.</li>
                </ul>
            </LegalSection>

            <LegalSection title="4. Acceptable Use">
                <p>You agree not to use the Service to:</p>
                <ul className="space-y-2">
                    <li>Violate any applicable law or regulation, or infringe any third party's rights;</li>
                    <li>Transmit spam, malware, or other harmful or unsolicited content;</li>
                    <li>Attempt to gain unauthorized access to the Service, other accounts, or our systems;</li>
                    <li>Reverse-engineer, decompile, or attempt to extract the source code of the Service, except where permitted by law;</li>
                    <li>Resell, sublicense, or white-label the Service without our prior written consent;</li>
                    <li>Use the AI Assistant to knowingly collect sensitive personal data from end users without a lawful basis and appropriate disclosures.</li>
                </ul>
            </LegalSection>

            <LegalSection title="5. AI-Generated Content">
                <p>
                    The AI Assistant, Pricing Intelligence recommendations, and Sentiment Analysis features use artificial
                    intelligence and machine-generated outputs. While we work to make these outputs accurate and useful, AI-generated
                    responses and recommendations may occasionally be incomplete, out of date, or incorrect. You are responsible for
                    reviewing AI-generated content before relying on it for pricing, compliance, or other business-critical
                    decisions.
                </p>
            </LegalSection>

            <LegalSection title="6. Subscriptions, Billing &amp; Trials">
                <ul className="space-y-2">
                    <li>Paid plans are billed in advance on a recurring basis (monthly or annually) as described at checkout or in your order form.</li>
                    <li>Trial periods, where offered, convert to a paid subscription unless cancelled before the trial ends.</li>
                    <li>Fees are non-refundable except where required by law or expressly stated otherwise.</li>
                    <li>We may change our pricing with advance notice; continued use of the Service after a price change takes effect constitutes acceptance of the new pricing.</li>
                </ul>
            </LegalSection>

            <LegalSection title="7. Intellectual Property">
                <p>
                    AIPA and its licensors retain all right, title, and interest in and to the Service, including all software,
                    designs, and trademarks. Subject to these Terms, we grant you a limited, non-exclusive, non-transferable license
                    to use the Service for your internal business purposes. You retain ownership of the content and data you submit
                    to the Service, including your knowledge base, listings, and lead data ("Customer Data").
                </p>
            </LegalSection>

            <LegalSection title="8. Data &amp; Privacy">
                <p>
                    Our collection and use of personal data in connection with the Service is described in our{' '}
                    <Link to="/legal/privacy">Privacy Policy</Link>, which forms part of these Terms. You are responsible for
                    ensuring you have the necessary rights and consents to submit any personal data of your own end users
                    (for example, visitors who chat with your AI Assistant) into the Service.
                </p>
            </LegalSection>

            <LegalSection title="9. Termination">
                <p>
                    You may cancel your subscription at any time from your account settings; cancellation takes effect at the end of
                    the current billing period. We may suspend or terminate your access to the Service if you materially breach
                    these Terms and do not cure the breach within a reasonable period after notice, or immediately in cases of
                    fraud, abuse, or security risk to the Service or other users.
                </p>
            </LegalSection>

            <LegalSection title="10. Disclaimers &amp; Limitation of Liability">
                <p>
                    The Service is provided "as is" and "as available," without warranties of any kind, express or implied, to the
                    fullest extent permitted by law. To the maximum extent permitted by law, AIPA will not be liable for any
                    indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, arising
                    out of or related to your use of the Service.
                </p>
            </LegalSection>

            <LegalSection title="11. Changes to These Terms">
                <p>
                    We may update these Terms from time to time. If we make material changes, we will provide reasonable notice,
                    such as by posting the updated Terms on this page with a new "Last updated" date. Continued use of the Service
                    after changes take effect constitutes acceptance of the revised Terms.
                </p>
            </LegalSection>

            <LegalSection title="12. Governing Law">
                <p>
                    These Terms are governed by the laws of the jurisdiction in which AIPA is established, without regard to
                    conflict-of-law principles, unless a different governing law is specified in a signed order form or agreement
                    between you and AIPA.
                </p>
            </LegalSection>

            <LegalSection title="13. Contact Us">
                <p>
                    Questions about these Terms can be sent to{' '}
                    <a href="mailto:hello@aipa.io">hello@aipa.io</a>, or via our <Link to="/company/contact">Contact page</Link>.
                </p>
            </LegalSection>
        </LegalPageLayout>
    );
}
